<style></style>

<template>
    <div class="page-container">
        <h1>Step 1: Assess</h1>
        <section-collapse accordion="step1assess-accordion" id="enrollment-why-its-important">
            <template #title>Why it's Important</template>
            <template #collapsed-content>
                <p>
                    Self-assessment allows child care providers to reflect on their current practices and begin thinking about goals for healthy change in their child care program. For Consultants and State Administrators, self-assessments identify areas of strength on which to build as well as areas needing greater support and resources.
                </p>
                <blockquote>
                    <div class="callout-dotted-border clearfix">
                        <div>"I feel like I was more credible going in there. By taking a self-assessment they got to see where they’re not meeting best practices and where they’re doing really well. I thought that was better than me just pointing things out. It was them acknowledging it and finding it out for themselves. And, they knew that it was evidence-based... Some of these self-assessments can be really eye-opening for directors. They think they are meeting the best practices when they’re really only meeting the bare minimum."</div>
                        <div class="float-right">Patty Sullivan, Go NAPSACC Consultant, NC</div>
                    </div>

                </blockquote>
            </template>
        </section-collapse>

        <section-collapse accordion="step1assess-accordion" id="enrollment-activities">
            <template #title>Activities</template>
            <template #collapsed-content>
                <p>Click any activity to read more</p>

                <activity-collapse>
                    <template #number>1</template>
                    <template #title>
                        <strong>Child care providers</strong> prepare to take self-assessments
                    </template>
                    <template #collapsed-content>
                        <div>
                            <p>Child care providers can start by reviewing a paper copy of the self-assessment first, which allows them to:</p>
                        </div>
                        <ul>
                            <li>Refer to menus, schedules, and policy documents</li>
                            <li>Ask teachers and staff about certain daily practices</li>
                            <li>Observe practices in real time</li>
                            <li>Take notes about the information collected</li>
                            <li>Identify questions where they need clarification from you</li>
                        </ul>
                        <provider-tools-spotlight class="mt-4">
                            <slot>
                                <h4>To print blank self-assessments…</h4>
                                <ol class="list-left-aligned">
                                    <li>Direct the child care provider to their Self-Assessment page.</li>
                                    <li>Look for the printer icon in top right corner. Click on Print a Blank Self-Assessment for a drop-down list of modules.</li>
                                    <li>Select the module to print.</li>
                                </ol>
                                <img :src="$basePath + '/images/ta-resources/1-assess/02 Step1Assess-Provider-To print blank self-assessments.png'" class="w-100 mb-4" style="margin-left: -12px">
                            </slot>
                        </provider-tools-spotlight>
                    </template>
                </activity-collapse>
                <activity-collapse>
                    <template #number>2</template>
                    <template #title>
                        <strong>Child care providers</strong> take online self-assessments
                    </template>
                    <template #collapsed-content>
                        <div>
                           <p>When ready, child care providers complete their self-assessments online.</p>
                        </div>
                        <ul>
                            <li>Self-assessments take 10-40 minutes, depending on the module and previous preparation.</li>
                            <li>Clicking on the Continue button at the bottom of the page saves their responses, allowing them to stop and resume the self-assessment without losing their work.</li>
                            <li>When they are finished, they must click the Finish and View Results button. No changes can be made after this point.</li>
                        </ul>
                    </template>
                </activity-collapse>
                <activity-collapse>
                    <template #number>3</template>
                    <template #title>
                        <strong>Child care providers</strong> view their results
                    </template>
                    <template #collapsed-content>
                        <div>
                           <p>After child care providers complete a self-assessment, they are presented with their results.</p>
                        </div>
                        <ul>
                            <li>Overall Results shows them how they are doing for that module.</li>
                            <li>Detailed Results show them sections where they are doing a great job and where they are on their way (but need more improvement).</li>
                            <li>Clicking on any section allows them to see a side-by-side comparison of how their current practices compare to best practices.</li>
                        </ul>
                    </template>
                </activity-collapse>
                <activity-collapse>
                    <template #number>4</template>
                    <template #title>
                        <strong>Consultant</strong> provides help and support
                    </template>
                    <template #collapsed-content>
                        <div>
                           <p>Help child care providers stay on track by encouraging them to complete self-assessments before the designated deadline. If self-assessments are not being completed on time, send a quick email/text prompt to remind them of the deadline. Then, check in by phone to discuss results.</p>
                        </div>
                        <strong class="text-primary">Check-in Prep:</strong>
                        <ul>
                            <li>Use your Consultant Tools to make sure that the recommended self-assessments are complete prior to the check-in.</li>
                            <li>Review their self-assessment results making note of strengths and areas for improvement.</li>
                            <li>Use the Add TA Activity button to record these notes for easy reference during the check-in. Mark the Activity Type as Planning Notes.</li>
                            <li>Review the agenda below and make adjustments or notes as needed.</li>
                        </ul>

                        <strong class="text-primary">Check-in Agenda:</strong>
                        <br />

                        <strong class="text-primary">Inquire about progress</strong>
                        <ul>
                            <li>“How are you doing with the self-assessments?”</li>
                            <li>“What questions do you have about the self-assessments items?”</li>
                        </ul>

                        <div class="pt-4"><strong>If the child care provider <u>has not completed</u> the recommended self-assessments:</strong></div>

                        <strong class="text-primary">Assess progress</strong>
                        <ul class="fa-ul">
                            <li>“How far along have you gotten on the self-assessments?”</li>
                        </ul>

                        <strong class="text-primary">Encourage reflection.</strong>
                        <ul class="fa-ul">
                            <li>“How are you feeling about the self-assessments so far?”</li>
                            <li> “What is getting in the way of completing them?”</li>
                        </ul>
                        
                        <strong class="text-primary">Discuss ways to overcome challenges and set a new timeline</strong>
                        <ul class="fa-ul">
                            <li>“I can see how <strong>[insert challenge]</strong> has been keeping you from finishing the self-assessments. What are some ways we can overcome this?”</li>
                            <li>If needed, offer technical support, ask if now is a good time to complete it, or encourage the child care provider to set aside a time when they or someone else on their staff can finish.</li>
                            <li>“Now that we have discussed some ways to overcome this challenge, how do you feel about finishing the self-assessment by <strong>[insert new date]</strong>?”</li>
                        </ul>
                        <div class="pt-4"><strong>If the child care provider <u>has completed</u> the recommended self-assessments:</strong></div>

                        <strong class="text-primary">Encourage reflection</strong>
                        <ul class="fa-ul">
                            <li>“How do you feel about your results?”</li>
                            <li>“What stood out to you?”</li>
                        </ul>
                            
                        <strong class="text-primary">Discuss results of each self-assessment</strong>
                        <ul class="fa-ul">
                            <li>“It looks like the overall results of your self-assessment fell under… (Room for Improvement, Making Progress, Leading the Way).”</li>
                            <li>Praise the child care provider for areas where they are meeting best practices. If no best practices are being met, offer praise for completing the self-assessments and areas they are closest to meeting best practices.</li>
                        </ul>

                        <strong class="text-primary">Discuss possible goals</strong> 
                        <p>Ask the child care provider about what goals they are considering. They should aim to identify three to five goals from each module. If they are working on multiple modules, such as Child Nutrition and Physical Activity, they would identify six to ten goals in total. Often, starting with easy wins is a great motivator.</p>
                        <ul class="fa-ul">
                            <li>“Did the self-assessment make you think of any new ideas for your program?”</li>
                            <li>“What goals may be a good fit for your program? Consider strengths that you and your program have that will help you succeed in making changes.”</li>
                            <li>If the child care provider has 15-30 minutes available, go on to setting goals and action planning. Otherwise, schedule an in-person check-in during the next couple of weeks to complete this next step.</li>
                        </ul>

                        <strong class="text-primary">Confirm date for the next check-in</strong>
                        <p>After the check-in, document your interaction and any notes using the Add TA Activity button.</p>

                        <br>

                        <helpful-hints>
                            <h5>Alleviate test anxiety</h5>
                            <p>Reassure child care providers that the self-assessment is not a test (or part of licensing) but a tool for their own learning and growth. Answering as completely and honestly as possible helps them see where they are now and allows them to set attainable goals.</p>
                            <h5>Provide clear steps to get started</h5>
                            <p>Suggest printing a paper copy of the self-assessment. Encourage them to take it one section at a time to avoid feeling overwhelmed.</p>
                            <h5>Manage feelings of disappointment</h5>
                            <p>Many child care providers feel motivated after taking self-assessments and seeing their results. However, for some the results can be surprising and disheartening. Remind them that they have made a great first step by participating in Go NAPSACC. There would be no reason for them to participate if they were already doing everything right!</p>
                        </helpful-hints>
                        <br>
                        <consultant-tools-spotlight>
                            <slot>
                                <h4>To view completed self-assessments…</h4>
                                <ol start="1" class="list-left-aligned">
                                    <li>Go to your My Programs page.</li>
                                    <li>Click on the child care program’s name.</li>
                                </ol>
                                <img :src="$basePath + '/images/ta-resources/1-assess/03 Step1Assess-Consultant-To view completed self-assessments-01.png'" class="w-100 mb-4">
                                <ol start="3" class="list-left-aligned">
                                    <li>You see a dashboard very similar to their My NAPSACC page.</li>
                                    <li>Use the My Progress bar to see a quick snapshot of completed self-assessments (first and most recent).</li>
                                    <li>Scroll down the page for access to all previously completed self-assessments.</li>
                                </ol>
                                <img :src="$basePath + '/images/ta-resources/1-assess/04 Step1Assess-Consultant-To view completed self-assessments-02.png'" class="w-100 mb-4">
                                <ol start="6" class="list-left-aligned">
                                    <li>Use the Add TA Activity button at the top right corner of the page to make a note.</li>
                                    <li>Under Activity Type, mark your entry as Planning Notes.</li>
                                    <li>Record key self-assessment results in the notes box.</li>
                                </ol>
                                <img :src="$basePath + '/images/ta-resources/1-assess/05 Step1Assess-Consultant-To view completed self-assessments-03.png'" class="w-100 mb-4">
                            </slot>
                        </consultant-tools-spotlight>
                    </template>
                </activity-collapse>
            </template>
        </section-collapse>

        <section-collapse accordion="step1assess-accordion" id="enrollment-common-challenges">
            <template #title>Common Website Challenges & Solutions</template>
            <template #collapsed-content>
                <h4 class="mt-0">Forgotten username or password</h4>
                    <p>Go NAPSACC can help retrieve username/email and reset password. The child care provider clicks on the blue Log In button at the top of the homepage. When the Log In box appears, they can click on Forgot Your Email or Forgot Your Password.</p>
                    <ul>
                        <li>Forgot Your Email allows the child care provider to look up the email address they registered with based on their name and the name and location of their child care program.</li>
                        <li>Forgot Your Password sends an email to the child care provider with a link and instructions for creating a new password.</li>
                        <li>If they are no longer able to access the email account listed and are unable to get logged in, contact Go NAPSACC at <a href="mailto:gonapsacc@unc.edu">gonapsacc@unc.edu</a> or (919)-843-3863. We try to respond as quickly as possible, but it is not always immediate.</li>
                    </ul>

                <h4 class="mt-0">Self-assessment questions don’t match the child care program’s characteristics</h4>
                    <p>Registration information is used to tailor self-assessment questions and action plans. Most of this tailoring is based on (a) whether the child care facility is a center/Head Start or a family child care home, (b) the ages of children served*, and (c) whether they are open for full or half day.</p>

                    <p>If self-assessment questions don’t look right, ask if the child care provider needs to update their information on their My Account page. However, they need to finish any open self-assessments before making corrections. They can retake self-assessments once information is corrected.</p>

                    <p>*All child care providers see the breastfeeding section of the Breastfeeding & Infant Feeding module, even if they don’t care for infants. This is to ensure that they have accommodations for breastfeeding staff or enrolled children’s family members.</p>

                <h4 class="mt-0">Unable to see completed self-assessments from my account</h4>
                    <p>You can only see completed self-assessments. The child care provider must answer questions and click on Finish and View Results at the end. You are then able to see their results.</p>

                <h4 class="mt-0">Difficulty printing self-assessment results</h4>
                    <p>The Go NAPSACC website opens a new tab in their browser with the printable version of their results. Their Internet browser may be blocking pop-ups. If so, search for instructions on how to allow pop-ups in the Internet browser they are using.</p>

                <h4 class="mt-0">New self-assessment accidentally started</h4>
                    <ul>
                        <li>If they have not answered any questions on the self-assessment, they should go to the summary page and click on Finish and View Results. The website ignores self-assessments that are completely blank.</li>
                        <li>If they started answering questions, encourage them to leave it open until it is time for another self-assessment. At that time, they can easily update any responses entered. In the meantime, they can access results from any completed self-assessments from their Self- Assessment page.</li>
                    </ul>
            </template>
        </section-collapse>

         <section-collapse accordion="step1assess-accordion" id="enrollment-common-challenges">
            <template #title>Common Technical Assistance Challenges & Solutions</template>
            <template #collapsed-content>
                <h4 class="mt-0">Assessing teacher practices when they vary across classrooms</h4>
                <p>Answers to the self-assessment should reflect norms for the whole center. The director may need to average across classrooms or average over time to account for this variability. When setting goals, the director can edit the action plans to reflect different steps for different classrooms, or the director may even set different goals for different classrooms.</p>

                <h4 class="mt-0">Self-assessment answers don’t match what I’ve observed</h4>
                <p>Child care providers may interpret the questions differently than intended. One way to support accuracy is to ask the child care provider to complete the self-assessment on paper first and then review it together. During this review meeting, it can be helpful to look at the menu, schedules, or play spaces together. If the child care provider has already completed the self-assessment, this type of review can still be helpful. For example, a menu review can be a great first step toward meeting Foods Provided goals.</p>

                <h4 class="mt-0">Self-assessments not completed on time</h4>
                <p>Monitor their progress with your Consultant Tools as deadlines approach. Send email or text reminders to help them stay on track. Ask about any challenges that you might help address.</p>
                
                <h4 class="mt-0">Non-English speaking</h4>
                <p>Go NAPSACC is currently available in English. Some paper-based Spanish-language resources are available, such as self-assessments (without tailoring). Consultants who speak both English and the child care provider’s native language may be able to help interpret tools and guide them through the Go NAPSACC process. Tools like Google Translate may also be useful but may not always provide the ideal translation.</p>
            </template>
        </section-collapse>

        <section-collapse accordion="step1assess-accordion" id="enrollment-support-documents">
            <template #title>Support Documents</template>
            <template #collapsed-content>
                <slot name="support-docs"><div>some backup text for support docs in enrollment</div></slot>
            </template>
        </section-collapse>

    </div>
</template>

<script>
    import SectionCollapse from '../../TaResources/SectionCollapse.vue';
    import HelpfulHints from '../../TaResources/HelpfulHints.vue';
    import ActivityCollapse from '../../TaResources/ActivityCollapse.vue';
    import ConsultantToolsSpotlight from '../../TaResources/ConsultantToolsSpotlight.vue';
    import ProviderToolsSpotlight from '../../TaResources/ProviderToolsSpotlight.vue';

    export default {
        components: {
            SectionCollapse,
            HelpfulHints,
            ActivityCollapse,
            ConsultantToolsSpotlight,
            ProviderToolsSpotlight,
        },
        data() {
            return {}
        },
        methods: {}
    
}
</script>